/* ============================================================
   FA Cart Drawer — fa-cart-drawer.js
   v2 — normalizes /cart/add.js response shapes.
   Shopify's newer cart backend answers form-encoded adds with
   {"items":[...]} instead of the legacy flat line item; which
   shape you get depends on the cart session. normalizeItem()
   accepts both, and renderAddedItem() falls back safely so a
   surprise shape can never print "undefined" / "$NaN".
   ============================================================ */
(function () {
  'use strict';

  var drawer, overlay, closeBtn, addedItemEl, recsEl, recsListEl;
  var isOpen = false;
  var isOurAdd = false;

  function money(cents) {
    if (typeof cents !== 'number' || !isFinite(cents)) return '';
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function fixUrl(url) {
    if (!url) return '';
    if (url.startsWith('//')) return 'https:' + url;
    return url;
  }

  /* Accept every response shape /cart/add.js is known to return:
     - legacy flat line item: { product_title, final_line_price, ... }
     - new cart backend / JSON adds: { items: [ {line item}, ... ] }
     Returns the line item object, or null if unrecognizable. */
  function normalizeItem(data) {
    if (!data) return null;
    if (data.items && data.items.length && typeof data.items[0] === 'object') {
      return data.items[0];
    }
    if (typeof data.product_title !== 'undefined' ||
        typeof data.variant_id !== 'undefined' ||
        typeof data.handle !== 'undefined') {
      return data;
    }
    return null;
  }

  /* Last-resort: the freshest line in /cart.js is items[0]. */
  function openDrawerFromCart(priceData) {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var item = cart && cart.items && cart.items[0] ? cart.items[0] : null;
        openDrawer(item, priceData);
      })
      .catch(function () { openDrawer(null, priceData); });
  }

  function capturePriceFromPage() {
    var sqftEl  = document.querySelector('.fa-price__mainprice');
    var unitEl  = document.querySelector('.fa-price__unit');
    var boxEl   = document.querySelector('.fa-price__box');
    var boxUnitEl = document.querySelector('.fa-price__boxunit');
    if (!sqftEl) return null;
    return {
      sqft:    sqftEl.textContent.trim(),
      sqftUnit: unitEl    ? unitEl.textContent.trim()    : '/ sq ft',
      box:     boxEl     ? boxEl.textContent.trim().split('\n')[0].trim() : null,
      boxUnit: boxUnitEl ? boxUnitEl.textContent.trim() : '/ box',
    };
  }

  function renderAddedItem(item, priceData) {
    /* Field fallbacks — line items differ slightly across cart API
       versions; never let a missing field reach the template. */
    var qty = item.quantity || 1;
    var title = item.product_title || item.title || 'Item';
    var itemUrl = item.url || '/cart';
    var finalPrice    = typeof item.final_price === 'number' ? item.final_price
                      : typeof item.price === 'number' ? item.price : null;
    var originalPrice = typeof item.original_price === 'number' ? item.original_price : finalPrice;
    var finalLine     = typeof item.final_line_price === 'number' ? item.final_line_price
                      : typeof item.line_price === 'number' ? item.line_price
                      : finalPrice !== null ? finalPrice * qty : null;
    var originalLine  = typeof item.original_line_price === 'number' ? item.original_line_price : finalLine;

    var hasDiscount = originalPrice !== null && finalPrice !== null && originalPrice !== finalPrice;
    var qtyLabel = qty + (qty === 1 ? ' box' : ' boxes');

    /* ── Price grid ── */
    var gridHTML = '';
    if (priceData && priceData.sqft) {
      var sqftOrig = hasDiscount ? '<span class="fa-drawer__price-orig">' + priceData.sqft + '</span>' : '';
      var sqftVal  = priceData.sqft;

      /* If discounted, calculate discounted per-sqft from item prices */
      if (hasDiscount) {
        var ratio = finalPrice / originalPrice;
        var origNum = parseFloat(priceData.sqft.replace(/[^0-9.]/g, ''));
        sqftVal = '$' + (origNum * ratio).toFixed(2);
      }

      var boxOrig = '';
      var boxVal  = priceData.box || money(finalPrice);
      if (hasDiscount && priceData.box) {
        boxOrig = '<span class="fa-drawer__price-orig">' + priceData.box + '</span>';
        boxVal  = money(finalPrice);
      }

      gridHTML = '<div class="fa-drawer__price-grid">'
        + '<div class="fa-drawer__price-cell">'
        + '<span class="fa-drawer__price-label">Per sq ft</span>'
        + sqftOrig
        + '<span class="fa-drawer__price-val' + (hasDiscount ? ' is-sale' : '') + '">' + sqftVal + '</span>'
        + '</div>'
        + '<div class="fa-drawer__price-cell">'
        + '<span class="fa-drawer__price-label">Per box</span>'
        + boxOrig
        + '<span class="fa-drawer__price-val' + (hasDiscount ? ' is-sale' : '') + '">' + boxVal + '</span>'
        + '</div>'
        + '</div>';
    }

    /* ── Total bar ── */
    var totalStr = money(finalLine);
    var totalHTML = '';
    if (totalStr) {
      totalHTML = '<div class="fa-drawer__total-bar">'
        + '<span class="fa-drawer__total-qty">' + qtyLabel + '</span>'
        + '<div class="fa-drawer__total-right">'
        + (hasDiscount ? '<span class="fa-drawer__total-orig">' + money(originalLine) + '</span>' : '')
        + '<span class="fa-drawer__total-val' + (hasDiscount ? '' : ' no-discount') + '">' + totalStr + '</span>'
        + '</div>'
        + '</div>';
    }

    /* ── Variant text ── */
    var optionParts = [];
    if (item.options_with_values) {
      item.options_with_values.forEach(function (o) {
        if (o.value !== 'Default Title') optionParts.push(o.value);
      });
    } else if (item.variant_title && item.variant_title !== 'Default Title') {
      optionParts.push(item.variant_title);
    }
    var variantText = optionParts.join(' · ');

    var imgSrc = fixUrl(item.image || (item.featured_image && item.featured_image.url) || '');
    if (imgSrc) imgSrc = imgSrc.replace(/(\.[a-z]+)(\?|$)/, '_160x160$1$2');

    addedItemEl.innerHTML = '<div class="fa-drawer__item-row">'
      + '<a href="' + itemUrl + '" class="fa-drawer__item-img-wrap">'
      + (imgSrc ? '<img src="' + imgSrc + '" alt="' + title + '" class="fa-drawer__item-img" loading="eager">' : '')
      + '</a>'
      + '<div class="fa-drawer__item-info">'
      + '<a href="' + itemUrl + '" class="fa-drawer__item-title">' + title + '</a>'
      + (variantText ? '<p class="fa-drawer__item-variant">' + variantText + '</p>' : '')
      + gridHTML
      + totalHTML
      + '</div>'
      + '</div>';
  }

  function renderRecs(products) {
    if (!products || !products.length) {
      recsEl.style.display = 'none';
      return;
    }

    recsListEl.innerHTML = products.map(function (p) {
      var price = p.variants && p.variants[0] ? parseInt(p.variants[0].price, 10) : 0;
      var imgSrc = fixUrl(typeof p.featured_image === 'string' ? p.featured_image : '');
      if (imgSrc) imgSrc = imgSrc.replace(/(\.[a-z]+)(\?|$)/, '_112x112$1$2');

      return '<a href="/products/' + p.handle + '" class="fa-drawer__rec-item">'
        + (imgSrc ? '<img src="' + imgSrc + '" alt="' + p.title + '" class="fa-drawer__rec-img" loading="lazy">' : '<div class="fa-drawer__rec-img"></div>')
        + '<div class="fa-drawer__rec-info">'
        + '<span class="fa-drawer__rec-title">' + p.title + '</span>'
        + '<span class="fa-drawer__rec-price">' + money(price) + '</span>'
        + '</div>'
        + '</a>';
    }).join('');

    recsEl.style.display = '';
  }

  function fetchRecs(productId) {
    if (!productId) return;
    fetch('/recommendations/products.json?product_id=' + productId + '&limit=6&intent=related')
      .then(function (r) { return r.json(); })
      .then(function (d) { renderRecs(d.products); })
      .catch(function () { recsEl.style.display = 'none'; });
  }

  function openDrawer(addedItem, priceData) {
    isOpen = true;
    addedItemEl.innerHTML = '<div class="fa-drawer__loading"><span class="fa-drawer__spinner"></span></div>';
    recsEl.style.display = 'none';
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    if (addedItem) {
      renderAddedItem(addedItem, priceData || null);
      fetchRecs(addedItem.product_id);
    } else {
      /* Never strand the spinner — confirm the add even without details. */
      addedItemEl.innerHTML = '<div class="fa-drawer__item-row">'
        + '<div class="fa-drawer__item-info">'
        + '<a href="/cart" class="fa-drawer__item-title">Item added to your cart.</a>'
        + '</div></div>';
    }
  }

  function closeDrawer() {
  if (!isOpen) return;
  isOpen = false;
  drawer.setAttribute('aria-hidden', 'true');
  document.body.style.overflow = '';
    }

  /* ── Intercept form submits ── */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.action || form.action.indexOf('/cart/add') === -1) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    var priceData = capturePriceFromPage();
    var submitBtn = form.querySelector('[name="add"], [type="submit"]');
    if (submitBtn) submitBtn.disabled = true;

    isOurAdd = true;

    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      body: new FormData(form)
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (d) { throw d; });
        return r.json();
      })
      .then(function (data) {
        var item = normalizeItem(data);
        if (item) openDrawer(item, priceData);
        else openDrawerFromCart(priceData);
      })
      .catch(function (err) {
        isOurAdd = false;
        if (err && err.description) alert(err.description);
        else form.submit();
      })
      .finally(function () {
        isOurAdd = false;
        if (submitBtn) submitBtn.disabled = false;
      });
  }, true);

  /* ── Fetch interceptor for quick-add / product cards ── */
  var origFetch = window.fetch;
  window.fetch = function (url, opts) {
    var result = origFetch.apply(this, arguments);
    var urlStr = typeof url === 'string' ? url : '';
    if (urlStr.indexOf('/cart/add') !== -1 && !isOurAdd) {
      result.then(function (res) {
        if (res.ok && window.location.pathname !== '/cart') {
          res.clone().json()
            .then(function (data) { openDrawer(normalizeItem(data), null); })
            .catch(function () { openDrawer(null, null); });
        }
      }).catch(function () {});
    }
    return result;
  };

  /* ── Init ── */
  document.addEventListener('DOMContentLoaded', function () {
    drawer = document.getElementById('fa-cart-drawer');
    if (!drawer) return;

    overlay   = document.getElementById('fa-drawer-overlay');
    closeBtn  = document.getElementById('fa-drawer-close');
    addedItemEl = document.getElementById('fa-drawer-added-item');
    recsEl    = document.getElementById('fa-drawer-recs');
    recsListEl = document.getElementById('fa-drawer-recs-list');

    overlay.addEventListener('click', closeDrawer);
    closeBtn.addEventListener('click', closeDrawer);
    document.getElementById('fa-drawer-continue')
      .addEventListener('click', closeDrawer);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen) closeDrawer();
    });
  });

  window.FACartDrawer = { open: openDrawer, close: closeDrawer };
})();